import React, { useEffect, useState, useMemo} from 'react';
import { Groupper, Icon, Label, Placeholder, Table, Header, Modal, Toolbar, Button } from 'react-frontier';
import { addCommas, bindClose, getCardBrandIcon, getCardTypeLocale } from '@payfront/components/Util';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { RefundModal, SegmentControl } from '../components';
import { useUser } from '../Hooks';
import { CardBrand, PaymentCardType } from '../DashboardClasses';
import API from '../API';
import NotFound from './NotFound';
import moment from 'moment';

enum Section{
	DETAILS = 0,
	REFUNDS = 1,
	EVENTS = 2,
	SERVICE = 3,
}

var TableHeader = (props: { text: string, colspan?: number })=>{
	return <Table.Row header>
		<Table.Cell colSpan={props.colspan || 2}>{props.text}</Table.Cell>
	</Table.Row>
}

var Value = (props: { value: any, width?: number, random?: boolean })=>{
	if(props.value){
		return <>{props.value}</>;
	}else{
		return <Placeholder width={props.width} random={props.random} />
	}
}

interface Payment{
	payment_id: number,
	client_id: number,
	client_name: string,
	service_id: number,
	service_name: string,
	service_bank: string,
	external_id: string,
	buyer_email: string,
	total: number,
	total_refunded: number,
	commission: number,
	paid: boolean,
	cancelled: boolean,
	cancel_reason: number,
	date_paid: number,
	date_cancelled: number,
	date_created: number,
	date_expiration: number,
	card: {
		card_id: number,
		card_name: string,
		card_digits: number,
		card_brand: CardBrand,
		card_type: PaymentCardType,
		card_bank: string,
		card_country: string,
		card_country_alpha2: string,
	},
	metadata: {
		metadata_id: number,
		metadata_key: string,
		metadata_value: string,
	}[],
	items: {
		item_id: number,
		item_name: string,
		item_metadata: string,
		item_external_id: string,
		item_price: number,
		item_quantity: number,
		item_image: string,
	}[]
}

interface PaymentRefund{
	refund_id: number,
	amount: number,
	date_created: number,
}

interface PaymentEvents{
	events: {
		log_id: number,
		action_id: number,
		action_name: string,
		params: string[],
		date_created: number,
	}[],
	webhooks: {
		response_id: number,
		webhook_id: number,
		event_id: number,
		response_status: number,
		date_try: number,
		date_created: number,
	}[]
}

interface PaymentServiceLog{
	log_id: number,
	service_id: number,
	service_name: string,
	operation: number,
	result: string,
	response: string,
	date_created: number,
}

enum ShownModal{
	REFUND = 1,
}

var PaymentView = ()=>{
	var { t } = useTranslation();
	var { user, admin } = useUser();
	var [shownSection, setShownSection] = useState<Section>(Section.DETAILS);
	var [payment, setPayment] = useState<Payment>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [sectionLoadError, setSectionLoadError] = useState<string>(null);
	var [refunds, setRefunds] = useState<PaymentRefund[]>(null);
	var [events, setEvents] = useState<PaymentEvents>(null);
	var [serviceLog, setServiceLog] = useState<PaymentServiceLog[]>(null);
	var [viewingRefund, setViewingRefund] = useState<number>(null);
	var [shownModal, setShownModal] = useState<ShownModal>(null);

	var params = useParams<{ payment_id: string }>();
	var payment_id = useMemo(()=>{
		var pid = parseInt(params.payment_id);
		if(Number.isNaN(pid)) return null;
		return pid;
	}, [params.payment_id]);

	var now = useMemo(()=>{
	return moment().unix();
}, []);

	useEffect(()=>{
		if(payment===null) API.scrollTop();
		if(payment_id===null) return;
		API.getPayment(payment_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setPayment(res.data);
		}).catch(err=>{
			return setLoadError(t('common.unexpected_error', { code: 'PYGET-1' }))
		})
	}, []);

	if(payment_id===null){
		return <NotFound />
	} 
	if(loadError){
		return <Header text={'Error'} subtext={loadError} style={{ marginTop: 15 }} />
	}

	var onSectionChange = (ix: number)=>{
		if(shownSection===ix) return;
		setShownSection(ix);
		setSectionLoadError(null);
		if(ix==Section.REFUNDS){
			getRefunds();
		}else if(ix===Section.EVENTS){
			getEvents();
		}else if(ix===Section.SERVICE){
			getProcessor()
		}
	}

	var getRefunds = ()=>{
		if(refunds) return;
		API.getPaymentRefunds(payment_id).then(res=>{
			if(res.error) return setSectionLoadError(res.message);
			setRefunds(res.data);
		}).catch(err=>{
			setSectionLoadError(t('common.unexpected_error', { code: 'PYRFL-1' }));
		})
	}

	var getEvents = ()=>{
		if(events) return;
		API.getPaymentEvents(payment_id).then(res=>{
			if(res.error) return setSectionLoadError(res.message);
			setEvents(res.data);
		}).catch(err=>{
			setSectionLoadError(t('common.unexpected_error', { code: 'PYEVL-1' }));
		})
	}

	var getProcessor = ()=>{
		if(serviceLog) return;
		API.getPaymentService(payment_id).then(res=>{
			if(res.error) return setSectionLoadError(res.message);
			setServiceLog(res.data);
		}).catch(err=>{
			setSectionLoadError(t('common.unexpected_error', { code: 'PYPRC-1' }));
		})
	}

	var showRefund = (refund_id: number)=>{
		return ()=>{
			setViewingRefund(refund_id);
		}
	}

	var sendRefund = ()=>{

	}

	return <div className="pf payment-view-screen">
		<Groupper title={t('payment.title')} className='details' fitted>
			<Toolbar>
				<Toolbar.Item text={t('payment.toolbar.refund')} iconName='reply' onClick={bindClose(setShownModal, ShownModal.REFUND)} />
				{!!user.admin && (
					<Toolbar.Dropdown text={t('payment.toolbar.admin_actions')} iconName='user-shield'>
						<Toolbar.Item text='Verificación antifraude' />
						<Toolbar.Item text='Ban BIN' />
					</Toolbar.Dropdown>
				)}
			</Toolbar>
			<Table details striped fitted className='first'>
				<Table.Row data={[t('payment.table.id'), params.payment_id]} />
				<Table.Row data={[t('payment.table.client'), <Value value={payment?.client_name} />]} />
				<Table.Row data={[t('payment.table.reference'), <Value value={payment ? (payment?.external_id || <Icon name='minus' />) : null} />]} />
				<Table.Row data={[t('payment.table.status'), (
					!payment ? (
						<Placeholder height={22} width={150} random={false} />
					) : (
						<Label color={(
							payment.total_refunded>0 ? 'orange' : (payment.cancelled ? 'red' : (payment.paid ? 'green' : (payment.date_expiration<now ? null : 'blue')))
						)}>{(
							payment.total_refunded>0 ? t(`common.${payment.total_refunded>=payment.total ? 'refunded' : 'partial_refund'}`) : (payment.cancelled ? t('common.cancelled') : (payment.paid ? t('common.paid') : (payment.date_expiration<now ? t('common.expired') : t('common.pending'))))
						)}</Label>
					)
				)]} />
				<Table.Divider />
				<Table.Row data={[t('payment.table.email'), <Value value={payment ? (payment?.buyer_email || <Icon name='minus' />) : null} />]} />
				<Table.Row data={[t('payment.table.date_created'), <Value value={payment ? moment.unix(payment.date_created).format('DD/MMM/YYYY HH:mm:ss') : null} width={110} random={false} />]} />
				<Table.Row data={[t('payment.table.date_paid'), <Value value={(
					payment ? (
						payment.paid ? moment.unix(payment.date_paid).format('DD/MMM/YYYY HH:mm:ss') : <Icon name='minus' />
					) : null
				)} width={110} random={false} />]} />
				<Table.Divider />
				<Table.Row data={[t('payment.table.total_paid'), <Value value={payment ? `$${addCommas(payment.total)}` : null} width={60} />]} />
				{payment?.commission>0 && (
					<Table.Row data={[t('payment.table.commission'), <Value value={payment ? `-$${addCommas(payment?.commission)}` : null} width={20} />]} />
				)}
				{(!!payment && payment.total_refunded>0) && (
					<Table.Row data={[t('payment.table.total_refunded'), (
						<span style={{ color: '#c76f11', fontWeight: 'bold' }}>-${addCommas(payment?.total_refunded)}</span>
					)]} />
				)}
				<Table.Row data={[t('payment.table.total_payout'), (
					payment ? (
						<span style={{ color: 'green', fontWeight: 'bold' }}>${addCommas(payment.total-(payment.commission || 0)-(payment.total_refunded || 0))}</span>
					) : (
						<Placeholder width={60} />
					)
				)]} />
			</Table>
		</Groupper>

		<SegmentControl
			onClick={onSectionChange}
			selected={shownSection!==null ? [shownSection] : []}
			segments={[
				{ value: Section.DETAILS, text: t('payment.sections.details') },
				...((payment && payment.total_refunded>0) ? [{
					value: Section.REFUNDS, text: t('payment.sections.refunds'),
				}] : []),
				{ value: Section.EVENTS, text: t('payment.sections.events') },
				...(user.admin ? [{
					value: Section.SERVICE, text: t('payment.sections.service')
				}] : []),
			]}
		/>
		<div className="sections">
			{sectionLoadError ? (
				<div className="pf container" style={{ marginBottom: 100 }}>
					<Header text={t('common.error')} subtext={sectionLoadError} />
				</div>
			) : (
				shownSection===Section.DETAILS ? (
					<Groupper title={t('payment.sections.details')} style={{ paddingBottom: 0 }}>
						<Table striped details fitted>
							<TableHeader text={t('common.card')} />
							<Table.Row data={[t('payment.card_info.id'), <Value value={payment?.card.card_id} />]} />
							<Table.Row data={[t('payment.card_info.card_name'), <Value value={payment?.card.card_name} />]} />
							<Table.Row data={[t('payment.card_info.card'), (
								payment ? <>
									<Icon name={getCardBrandIcon(payment.card.card_brand)} brand />
									•••• {payment.card.card_digits}
								</> : (
									<Placeholder />
								)
							)]} />
							<Table.Row data={[t('payment.card_info.card_type'), <Value value={payment ? (
								getCardTypeLocale(payment.card.card_type) || <Icon name='minus' />
							) : null} />]} />
							<Table.Row data={[t('payment.card_info.card_bank'), <Value value={payment ? (payment?.card.card_bank || <Icon name='minus' />) : null} />]} />
							<Table.Row data={[t('payment.card_info.card_country'), <Value value={payment ? (payment?.card.card_country || <Icon name='minus' />) : null} />]} />
	
							{!!payment && !!payment.metadata && payment.metadata.length>0 && <>
								<TableHeader text={t('payment.card_info.metadata')} />
								{payment.metadata.map(a=>(
									<Table.Row key={`PYMT-${a.metadata_id}`} data={[a.metadata_key, a.metadata_value]} />
								))}
							</>}
							{!!user.admin && !!payment && <>
								<TableHeader text={t('payment.admin.title')} />
								<Table.Row data={[t('payment.admin.service'), <>
									{payment.service_name}
									<div className="meta">ID: {payment.service_id}</div>
								</>]} />
								<Table.Row data={[t('payment.admin.service_bank'), <>
									{payment.service_bank}
								</>]} />
							</>}
							{!!payment && !!payment.items && payment.items.length>0 && (
								<TableHeader text={t('payment.card_info.items')} />
							)}
						</Table>
						{!!payment && !!payment.items && payment.items.length>0 && (
							<Table
								striped 
								className='products'
								centeredIndexes={[1]}
								headers={[
									t('payment.products.name'), 
									'#', 
									t('payment.products.subtotal'), 
									t('payment.products.total'),
								]}
								collapsingIndexes={[1,2,3]}
							>
								{payment.items.map(a=>(
									<Table.Row key={`PYITM-${a.item_id}`} collapsingIndexes={[1,2,3]}>
										<Table.Cell>
											<div className="id">ID: {a.item_external_id}</div>
											<div className="name">{a.item_name}</div>
											{!!a.item_metadata && a.item_metadata.length>0 && (
												<div className="metadata">{a.item_metadata}</div>
											)}
										</Table.Cell>
										<Table.Cell style={{ textAlign: 'center' }}>{a.item_quantity}</Table.Cell>
										<Table.Cell>${addCommas(a.item_price, true, true)}</Table.Cell>
										<Table.Cell>${addCommas(a.item_price*a.item_quantity)}</Table.Cell>
									</Table.Row>
								))}
							</Table>
						)}
					</Groupper>
				) : shownSection===Section.REFUNDS ? (
					refunds===null ? (
						<Header text={t('common.loading')} loading />
					) : refunds.length==0 ? (
						<Header text={t('payment.no_refunds')} subtext={t('payment.no_refunds_text')} />
					) : (
						<Table
							selectable
							striped
							title={t('payment.sections.refunds')}
							collapsingIndexes={[0, 2]}
							headers={['ID', t('common.date'), t('common.quantity')]}
							centeredIndexes={[2]}
						>
							{refunds.map(a=>(
								<Table.Row
									onClick={showRefund(a.refund_id)}
									key={`PYRFD-${a.refund_id}`}
									data={[a.refund_id, moment.unix(a.date_created).format('DD/MM/YYYY HH:mm'), `$${addCommas(a.amount)}`]}
								/>
							))}
						</Table>
					)
				) : shownSection===Section.EVENTS ? (
					<Groupper title={t('payment.sections.events')} fitted>
						<Table striped collapsingIndexes={[0, 2]} headers={['ID', t('common.event'), t('common.date')]}>
							{events ? (
								events.events.length==0 ? (
									<Table.Cell row empty colSpan={100}>{t('payment.no_events')}</Table.Cell>
								) : events.events.map(a=>(
									<Table.Row key={`EVLG-${a.log_id}`} collapsingIndexes={[0, 2]} data={[
										a.log_id, 
										<>
											{t(`log_events.event${a.action_id}`, {
												defaultValue: t('log_events.event_invalid', { code: a.action_id })
											})}
											{!!(a.params && a.params.filter(a=>!!a).length>0) && <>
												<div style={{ color: 'gray', fontSize: 12, lineHeight: '10px' }}>{a.params.filter(a=>!!a).join(', ')}</div>
											</>}
										</>,
										moment.unix(a.date_created).format('DD/MM/YYYY HH:mm:ss')
									]} />
								))
							) : (
								new Array(3).fill('0').map((a, i)=>(
									<Table.Row key={`EVLGPLH-${i}`} collapsingIndexes={[0, 2]} data={[
										<Placeholder width={85} random={false} />,
										<Placeholder />,
										<Placeholder width={120} random={false} />
									]} />
								))
							)}
						</Table>
						<Groupper.Divider text={t('common.webhooks')} />
						<Table collapsingIndexes={[0, 2, 3]} headers={[t('common.event'), t('common.date'), t('common.status')]}>
							{events ? (
								events.webhooks.length==0 ? (
									<Table.Cell row empty colSpan={100}>{t('payment.no_webhooks')}</Table.Cell>
								) : events.webhooks.map(a=>(
									<Table.Row 
										key={`WBHK-${a.response_id}`}
										selectable compact
										as={Link}
										to={`/pagos/${payment_id}/webhook/${a.response_id}`}
										collapsingIndexes={[0, 2]} 
										data={[
											<>
												<div className="id">ID: {a.response_id}</div>
												{t(`webhook_events.event${a.event_id}`, {
													defaultValue: t('webhook_events.event_invalid', { code: a.event_id })
												})}
											</>,
											moment.unix(a.date_try || a.date_created).format('DD/MM/YYYY HH:mm:ss'),
											<>
												<Icon name={a.response_status==200 ? 'check' : 'remove'} color={a.response_status==200 ? 'green' : 'brown'} />
												<span style={{ color: a.response_status==200 ? 'green' : 'brown' }}>{a.response_status}</span>
											</>
										]} 
									/>
								))
							) : (
								<Table.Row collapsingIndexes={[0, 2, 3]} data={[
									<Placeholder width={85} random={false} />,
									<Placeholder />,
									<Placeholder width={120} random={false} />,
									<Placeholder width={50} random={false} />
								]} />
							)}
						</Table>
					</Groupper>
				) : shownSection===Section.SERVICE ? (
					<Table
						title='Service Log' 
						collapsingIndexes={[0, 1, 2, 4]} 
						headers={['ID', 'Service', 'Operacion', 'Respuesta', 'Fecha']}
					>
						{serviceLog ? serviceLog.map(a=>(
							<Table.Row
								compact selectable
								as={Link}
								key={`SRV-${a.log_id}`}
								to={`/pagos/${payment_id}/service/${a.log_id}`}
								collapsingIndexes={[0, 1, 2, 4, 5]}
								data={[
									a.log_id,
									a.service_name,
									a.operation,
									<div style={{ lineHeight: '12px', fontSize: 12 }}>{a.response}</div>,
									moment.unix(a.date_created).format('DD/MM/YY HH:mm:ss')
								]}
							/>
						)) : (
							new Array(3).fill('0').map((a, i)=>(
								<Table.Row key={`SRVPLH-${i}`} collapsingIndexes={[0, 2, 3]} data={[
									<Placeholder width={85} random={false} />,
									<Placeholder width={30} />,
									<Placeholder width={40} />,
									<Placeholder />,
									<Placeholder width={50} random={false} />
								]} />
							))
						)}
					</Table>
				) : null
			)}
		</div>
		<Modal open={shownModal===ShownModal.REFUND} onClose={bindClose(setShownModal)} size='tiny'>
			<Modal.Header>{t('payment.refund.title')}</Modal.Header>
			<Modal.Content>

			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} onClick={bindClose(setShownModal)} basic />
				<Button text={t('payment.refund.title')} color='blue' onClick={sendRefund} />
			</Modal.Actions>
		</Modal>
		<RefundModal open={viewingRefund!==null} refundId={viewingRefund} onClose={bindClose(setViewingRefund)} />
	</div>
}

export default PaymentView;